import styled from 'styled-components';

import { CUST_ANIM, CUST_REDUCED_MOTION } from '../../../../theme/customizer-tokens';

/** Slide direction for an entering step: forward from the right, back from the
 *  left, none (fade only) for an unknown first paint. */
export type StepSlideDir = 'forward' | 'back' | 'none';

const slideOffset = (dir: StepSlideDir): number => {
  if (dir === 'forward') return CUST_ANIM.STEP_SLIDE_OFFSET_PX; // new step arrives from the right
  if (dir === 'back') return -CUST_ANIM.STEP_SLIDE_OFFSET_PX; // arrives from the left
  return 0; // fade only
};

/**
 * Wraps the active step's body so it slides + fades IN on each step change
 * (driven by `MobileStepSlide`). Slide-in only — the previous step unmounts with
 * its route, so there is no outgoing layer to animate (the app's lightweight
 * pattern). It uses `min-height: 100%` (NOT `height: 100%`) so it still fills the
 * scrollable Body when a step is short, but GROWS past the viewport when a step's
 * content is tall, letting the Body (the scroll container) actually scroll to the
 * last element. A hard `height: 100%` clamps the wrapper to the viewport on iOS,
 * clipping the reserved CTA-clearance padding so the final control (e.g. "I have my
 * own name") stays trapped under the fixed CTA and can't be scrolled into view. The
 * resting transform is `none` so it leaves no transform containing block once
 * settled; reduced motion drops the horizontal travel.
 */
export const Slide = styled.div<{ $dir: StepSlideDir; $entered: boolean }>`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  will-change: transform, opacity;
  transition:
    transform ${CUST_ANIM.STEP_SLIDE_MS}ms ${CUST_ANIM.SCREEN_EASE},
    opacity ${CUST_ANIM.STEP_SLIDE_MS}ms ${CUST_ANIM.SCREEN_EASE};
  opacity: ${({ $entered }) => ($entered ? 1 : 0)};
  transform: ${({ $dir, $entered }) => ($entered ? 'none' : `translate3d(${slideOffset($dir)}px, 0, 0)`)};

  ${CUST_REDUCED_MOTION} {
    transition: opacity ${Math.round(CUST_ANIM.STEP_SLIDE_MS / 2)}ms ${CUST_ANIM.SCREEN_EASE};
    transform: none;
  }
`;
